import { useMutation, useQueryClient } from '@tanstack/react-query';
import { usersService } from '../../../services';
import type { EditUserPayload } from '../../../services';

export const EMAIL_IN_USE_FALLBACK_MESSAGE =
  'Este e-mail já está cadastrado em outro usuário.';

/** O e-mail pedido já é o login de outro usuário (409 da Api). */
export class EmailAlreadyInUseError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'EmailAlreadyInUseError';
  }
}

/** A Api recusou o e-mail pedido (400 em `errors.email`, ex.: caractere). */
export class InvalidEmailError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'InvalidEmailError';
  }
}

/** Listas que exibem usuários: todas refletem a edição na hora. */
const USER_LIST_QUERY_KEYS = [
  ['users'],
  ['brokers-users'],
  ['broker-users'],
  ['advisories-users'],
] as const;

/**
 * Chama a Api e traduz a resposta: 409 vira {@link EmailAlreadyInUseError} e
 * e-mail recusado na validação vira {@link InvalidEmailError}.
 */
export async function updateUserOrThrow(payload: EditUserPayload) {
  const response = await usersService.updateUser(payload);
  if (response.emailInUse) {
    throw new EmailAlreadyInUseError(
      response.error || EMAIL_IN_USE_FALLBACK_MESSAGE
    );
  }
  if (response.emailError) {
    throw new InvalidEmailError(response.emailError);
  }
  if (!response.success) {
    throw new Error(response.error || 'Erro ao atualizar usuário');
  }
  return response.data;
}

export function useUpdateUser() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: updateUserOrThrow,
    onSuccess: async () => {
      await Promise.all(
        USER_LIST_QUERY_KEYS.map(queryKey =>
          queryClient.invalidateQueries({ queryKey: [...queryKey] })
        )
      );
    },
  });
}
